import clsx from "clsx";
import PropTypes from "prop-types";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useMutation } from "@apollo/client";
import { Box, Button, Divider, Icon } from "@mui/material";
import { makeStyles } from "@mui/styles";

import { setTheme } from "../../../actions";
import { UPDATE_ACCOUNT_SETTINGS } from "../../../graphql/mutations";
import { useSnackbar } from "../../../hooks";
import ConfirmationModal from "../../common/ConfirmationModal";
import AccountTabPanel from "./AccountTabPanel";
import BannerSection from "./BannerSection";

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.common.white,
  },
  modalFooter: {
    padding: "10px 20px",
    borderTop: "1px solid #ddd",
  },
  btn: {
    padding: "8px 15px",
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      opacity: 0.8,
    },
  },
  btnIcon: {
    fontSize: 14,
    marginRight: 8,
    verticalAlign: "middle",
  },
  formControl: {
    marginBottom: 25,
    "& .MuiGrid-item": {
      paddingTop: 0,
      paddingBottom: 0,
    },
    "& .MuiFormControl-marginDense": {
      marginTop: 3,
    },
  },
  text: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginBottom: 20,
  },
  divider: {
    marginTop: 4,
    marginBottom: 0,
  },
  settingWrapper: {
    padding: "5px 20px 20px 20px",
    [theme.breakpoints.down("sm")]: {
      padding: "5px 8px 20px 8px",
    },
  },
}));

const AccountSettings = ({ accountSettings, onSaved, setAccountSettings, setIsViewedAs }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();

  const snackbar = useSnackbar();

  const [draftAddress, setDraftAddress] = useState(false);

  const [confirmModal, setConfirmModal] = useState(false);
  const [
    showAddressChangeRequestSentModal,
    setShowAddressChangeRequestSentModal,
  ] = useState(false);

  const [updateAccountSettings] = useMutation(UPDATE_ACCOUNT_SETTINGS);

  const handeCloseConfirmModal = () => {
    setConfirmModal(false);
  };

  const handleSaveAccountSettings = async () => {
    try {
      setDraftAddress(false);

      const model = { ...accountSettings };

      delete model.allowEditAddresses;
      delete model.allowEditSubscriptionLevel;
      delete model.avatarUrl;
      delete model.companyRoleName;
      delete model.companyUserGroup;
      delete model.userGroupBreadCrumb;
      delete model.deliveryAddressSettings;
      delete model.invoiceAddressSettings;      
      delete model.postalAddressSettings;
      delete model.visitingAddressSettings;
      delete model.enabledMFATypes;

      const results = await updateAccountSettings({
        variables: { model },
      });

      const result = results?.data?.userAccountContext?.updateAccountSettings;

      if (result?.success) {
        snackbar.success(<FormattedMessage id="settings.updateSuccess" />);

        if (
          results.data.userAccountContext.updateAccountSettings
            .addressChangeRequestSent
        ) {
          setShowAddressChangeRequestSentModal(true);
        }

        dispatch(
          setTheme({
            theme:
              accountSettings.selectedTheme !== "0"
                ? accountSettings.selectedTheme
                : "",
          })
        );

        if (accountSettings.selectedTheme !== "0") {
          const viewer = JSON.parse(localStorage.getItem("viewer"));
          viewer.defaultTheme = accountSettings.selectedTheme;
          localStorage.setItem("viewer", JSON.stringify(viewer));
        }
      }
      else {
        snackbar.workspaceError(
          intl.formatMessage({ id: "settings.updateFailure" })
        );
      }

      if (confirmModal) {
        handeCloseConfirmModal();
      }

      if (result?.success) {
        onSaved?.();
      }
    } catch (err) {
      console.error("error >", err);
      snackbar.workspaceError(
        intl.formatMessage({ id: "settings.updateFailure" })
      );
    }
  };

  const [showSaveButton, setShowButton] = useState(true);

  const handleTabChange = (tabValue) => {
    if (tabValue == 3) {
      setShowButton(false);
    }
    else {
      setShowButton(true);
    }
  }

  return (
    <>
      <BannerSection
        setIsViewedAs={setIsViewedAs}
        accountSettings={accountSettings}
        setAccountSettings={setAccountSettings}
      />
      <Box className={classes.settingWrapper}>
        <Divider className={classes.divider} />
        <AccountTabPanel
          accountSettings={accountSettings}
          setAccountSettings={setAccountSettings}          
          handleSaveAccountSettings={handleSaveAccountSettings}
          setDraftAddress={setDraftAddress}
          onTabChange={handleTabChange}
        />
        {showSaveButton && (
          <Box align="right">
            <Button
              className={classes.btn}
              onClick={() =>
                draftAddress ? setConfirmModal(true) : handleSaveAccountSettings()
              }              
            >
              <Icon className={clsx(["fa fa-save", classes.btnIcon])} />
              <FormattedMessage id="btn.save" />
            </Button>
          </Box>)}
      </Box>
      {confirmModal && (
        <ConfirmationModal
          open={confirmModal}
          handleClose={handeCloseConfirmModal}
          onClick={handleSaveAccountSettings}
          message={<FormattedMessage id="addresses.saveChanges" />}
        />
      )}
      {showAddressChangeRequestSentModal && (
        <ConfirmationModal
          open={showAddressChangeRequestSentModal}
          message={<FormattedMessage id="settings.addressWarning" />}
          handleClose={() => setShowAddressChangeRequestSentModal(false)}
          variant="secondary"
        />
      )}
    </>
  );
};

AccountSettings.defaultProps = {
  accountSettings: {},
};

AccountSettings.propTypes = {
  accountSettings: PropTypes.shape({
    selectedTheme: PropTypes.string,
  }),
  onSaved: PropTypes.func,
  setAccountSettings: PropTypes.func.isRequired,
  setIsViewedAs: PropTypes.func.isRequired,
};

export default AccountSettings;
